import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useAuthContext } from '../../../hooks/useAuthContext';
import { toggleLike } from '../../../services/firebase/forum';
import AnimatedFlameIcon from './AnimatedFlameIcon';
import { isMobileDevice, triggerHapticFeedback } from '../../../utils/mobileUtils';
import { createIOSSafariClickHandler } from '../../../utils/iosSafariTouchFix';

/**
 * Background like processing with retry logic
 * Handles async like operations that persist across navigation
 */
const processLikeAsync = async (userId, targetType, targetId, newIsLiked, newLikeCount, previousIsLiked, previousLikeCount, likeKey) => {
  const maxRetries = 3;
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      console.log(`🔥 Processing like async (attempt ${retryCount + 1}):`, { userId, targetType, targetId, newIsLiked });
      
      const result = await toggleLike(userId, targetType, targetId);
      
      if (result.success) {
        const serverIsLiked = result.data.action === 'liked';
        const serverLikeCount = result.data.likeCount;
        
        console.log('🔥 Like operation successful:', { serverIsLiked, serverLikeCount });
        
        return {
          success: true,
          serverData: {
            isLiked: serverIsLiked,
            likeCount: serverLikeCount
          },
          shouldRollback: false
        };
      } else {
        console.error('🔥 Server rejected like operation:', result.error);
        
        // If it's a validation error, don't retry
        if (result.error.includes('not found') || result.error.includes('Invalid')) {
          return {
            success: false,
            shouldRollback: true,
            error: result.error
          };
        }
        
        // For other errors, retry
        retryCount++;
        if (retryCount < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Exponential backoff
          console.log(`🔥 Retrying like operation in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    } catch (error) {
      console.error('🔥 Like operation failed:', error);
      retryCount++;
      
      if (retryCount < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
        console.log(`🔥 Retrying like operation in ${delay}ms after error...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  // All retries failed - queue for background retry
  console.error('🔥 All like operation retries failed, queuing for background processing');
  queueFailedLikeOperation(userId, targetType, targetId, newIsLiked, likeKey);
  
  return {
    success: false,
    shouldRollback: true,
    error: 'Network error - operation will be retried in background'
  };
};

/**
 * Queue failed like operations for background retry
 */
const queueFailedLikeOperation = (userId, targetType, targetId, isLiked, likeKey) => {
  const queueKey = 'failed_like_operations';
  const existingQueue = JSON.parse(localStorage.getItem(queueKey) || '[]');
  
  // Check if operation already exists in queue
  const existingIndex = existingQueue.findIndex(op => 
    op.userId === userId && op.targetType === targetType && op.targetId === targetId
  );
  
  const operation = {
    userId,
    targetType,
    targetId,
    isLiked,
    likeKey,
    timestamp: Date.now(),
    retryCount: 0
  };
  
  if (existingIndex !== -1) {
    // Update existing operation
    existingQueue[existingIndex] = operation;
  } else {
    // Add new operation
    existingQueue.push(operation);
  }
  
  localStorage.setItem(queueKey, JSON.stringify(existingQueue));
  
  // Schedule background retry
  setTimeout(() => processFailedLikeQueue(), 5000);
};

/**
 * Process queued failed like operations
 */
const processFailedLikeQueue = async () => {
  const queueKey = 'failed_like_operations';
  const queue = JSON.parse(localStorage.getItem(queueKey) || '[]');
  
  if (queue.length === 0) return;
  
  console.log(`🔥 Processing ${queue.length} failed like operations from queue`);
  
  const updatedQueue = [];
  
  for (const operation of queue) {
    const { userId, targetType, targetId, isLiked, likeKey, retryCount } = operation;
    
    // Skip operations older than 1 hour
    if (Date.now() - operation.timestamp > 3600000) {
      console.log('🔥 Skipping expired like operation:', operation);
      localStorage.removeItem(likeKey);
      continue;
    }
    
    try {
      const result = await toggleLike(userId, targetType, targetId);
      
      if (result.success) {
        console.log('🔥 Background like operation successful:', operation);
        localStorage.removeItem(likeKey);
        // Don't add back to queue
      } else {
        if (retryCount < 5) {
          // Retry with exponential backoff
          operation.retryCount = retryCount + 1;
          operation.timestamp = Date.now();
          updatedQueue.push(operation);
        } else {
          console.error('🔥 Giving up on like operation after 5 retries:', operation);
          localStorage.removeItem(likeKey);
        }
      }
    } catch (error) {
      if (retryCount < 5) {
        operation.retryCount = retryCount + 1;
        operation.timestamp = Date.now();
        updatedQueue.push(operation);
      } else {
        console.error('🔥 Giving up on like operation after 5 retries due to error:', error);
        localStorage.removeItem(likeKey);
      }
    }
  }
  
  localStorage.setItem(queueKey, JSON.stringify(updatedQueue));
  
  // Schedule next processing if there are still items in queue
  if (updatedQueue.length > 0) {
    setTimeout(() => processFailedLikeQueue(), 30000); // Retry every 30 seconds
  }
};

/**
 * Reusable LikeButton component using the burnt icon as like symbol
 * Supports both questions and replies with optimistic updates and rollback on failure
 * Enhanced with text labels and larger icons for improved visibility and clarity
 */
const LikeButton = ({ 
  targetType, 
  targetId, 
  initialLikeCount = 0, 
  authorId,
  onLikeUpdate,
  size = 'md',
  showCount = true,
  showText = true,
  textVariant = 'full',
  customText,
  className = ''
}) => {
  const { user } = useAuthContext();
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  const [isLiking, setIsLiking] = useState(false);
  const [optimisticState, setOptimisticState] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  
  const isMobile = useMemo(() => isMobileDevice(), []);

  // Layout validation to prevent overflow
  const validateLayout = useCallback((textVariant, containerSize, isMobile) => {
    // For very small containers or mobile compact contexts, use compact mode
    if (containerSize === 'sm' && isMobile && textVariant === 'full') {
      return 'compact';
    }
    
    // If text is disabled, always use icon-only
    if (!showText) {
      return 'icon-only';
    }
    
    return textVariant;
  }, [showText]);

  // Validated text variant to prevent layout issues
  const validatedTextVariant = useMemo(() => 
    validateLayout(textVariant, size, isMobile),
    [textVariant, size, isMobile, validateLayout]
  );

  // Enhanced size configurations with larger icons and text support
  const sizeConfig = useMemo(() => ({
    sm: {
      icon: 'w-4 h-4',        // increased from w-3 h-3
      text: 'text-xs',
      button: isMobile ? 'px-3 py-2 min-h-[44px]' : 'px-2 py-1',
      gap: validatedTextVariant === 'icon-only' ? 'space-x-1' : 'space-x-1.5',
      minWidth: validatedTextVariant === 'icon-only' 
        ? (isMobile ? 'min-w-[44px]' : 'min-w-[36px]')
        : (isMobile ? 'min-w-[80px]' : 'min-w-[70px]'),
      maxWidth: 'max-w-[120px]'  // prevent excessive width
    },
    md: {
      icon: 'w-5 h-5',        // increased from w-4 h-4
      text: 'text-sm',
      button: isMobile ? 'px-4 py-2 min-h-[44px]' : 'px-3 py-2',
      gap: validatedTextVariant === 'icon-only' ? 'space-x-1' : 'space-x-2',
      minWidth: validatedTextVariant === 'icon-only' 
        ? (isMobile ? 'min-w-[44px]' : 'min-w-[40px]')
        : (isMobile ? 'min-w-[90px]' : 'min-w-[80px]'),
      maxWidth: 'max-w-[140px]'
    },
    lg: {
      icon: 'w-6 h-6',        // increased from w-5 h-5
      text: 'text-base',
      button: isMobile ? 'px-5 py-3 min-h-[48px]' : 'px-4 py-2',
      gap: validatedTextVariant === 'icon-only' ? 'space-x-1' : 'space-x-2.5',
      minWidth: validatedTextVariant === 'icon-only' 
        ? (isMobile ? 'min-w-[48px]' : 'min-w-[44px]')
        : (isMobile ? 'min-w-[100px]' : 'min-w-[90px]'),
      maxWidth: 'max-w-[160px]'
    }
  }), [isMobile, validatedTextVariant]);

  const config = sizeConfig[size] || sizeConfig.md;

  // Text display logic
  const getDisplayText = useCallback((isLiked, textVariant, customText) => {
    if (textVariant === 'icon-only' || !showText) return null;
    
    // When liked, don't show any text (icon-only mode)
    // When not liked, show "Like" text
    if (isLiked) return null;
    
    const defaultText = {
      like: 'Like',
      liked: null // No text when liked
    };
    
    const text = customText || defaultText;
    return text.like;
  }, [showText]);

  // Get responsive text class based on size and mobile context
  const getResponsiveTextClass = useCallback((size, isMobile) => {
    const baseClasses = {
      sm: 'text-xs',
      md: 'text-sm', 
      lg: 'text-base'
    };
    
    const mobileAdjustments = isMobile ? {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-sm'  // Prevent text from being too large on mobile
    } : {};
    
    return mobileAdjustments[size] || baseClasses[size];
  }, []);

  // Current display text using validated variant
  const displayText = useMemo(() => 
    getDisplayText(isLiked, validatedTextVariant, customText), 
    [isLiked, validatedTextVariant, customText, getDisplayText]
  );

  // Responsive text class
  const textClass = useMemo(() => 
    getResponsiveTextClass(size, isMobile), 
    [size, isMobile, getResponsiveTextClass]
  );

  // Check if user has liked this content and restore optimistic state
  useEffect(() => {
    const checkUserLike = async () => {
      if (user && targetId) {
        // First check for optimistic state in localStorage
        const likeKey = `like_optimistic_${user.uid}_${targetType}_${targetId}`;
        const optimisticData = localStorage.getItem(likeKey);
        
        if (optimisticData) {
          try {
            const parsed = JSON.parse(optimisticData);
            // If optimistic state is recent (within 30 seconds), use it
            if (Date.now() - parsed.timestamp < 30000) {
              console.log('🔥 Restoring optimistic like state:', parsed);
              setIsLiked(parsed.isLiked);
              setLikeCount(parsed.likeCount);
              setOptimisticState({ isLiked: parsed.isLiked, likeCount: parsed.likeCount });
              
              // Continue processing the operation in background
              processLikeAsync(
                user.uid, 
                targetType, 
                targetId, 
                parsed.isLiked, 
                parsed.likeCount, 
                parsed.previousState.isLiked, 
                parsed.previousState.likeCount, 
                likeKey
              ).then(({ success, serverData, shouldRollback }) => {
                if (shouldRollback) {
                  setIsLiked(parsed.previousState.isLiked);
                  setLikeCount(parsed.previousState.likeCount);
                  setOptimisticState(null);
                } else if (success && serverData) {
                  setIsLiked(serverData.isLiked);
                  setLikeCount(serverData.likeCount);
                  setOptimisticState(null);
                }
                localStorage.removeItem(likeKey);
              }).catch(error => {
                console.error('Error processing restored optimistic state:', error);
              });
              
              return; // Don't fetch from server if we have recent optimistic state
            } else {
              // Clean up old optimistic state
              localStorage.removeItem(likeKey);
            }
          } catch (error) {
            console.error('Error parsing optimistic state:', error);
            localStorage.removeItem(likeKey);
          }
        }
        
        // Fetch actual state from server
        try {
          const { checkUserLike } = await import('../../../services/firebase/forum');
          const result = await checkUserLike(user.uid, targetType, targetId);
          if (result.success) {
            setIsLiked(result.data.hasLiked);
          }
        } catch (error) {
          console.error('Error checking user like:', error);
        }
      }
    };

    checkUserLike();
    
    // Start processing any existing failed operations when component mounts
    if (user) {
      setTimeout(() => processFailedLikeQueue(), 1000);
    }
  }, [user, targetId, targetType]);

  // Update like count when prop changes
  useEffect(() => {
    setLikeCount(initialLikeCount);
  }, [initialLikeCount]);

  // Enhanced mobile touch handler with async background processing
  const handleLike = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent double-tap zoom on mobile
    if (isMobile) {
      e.preventDefault();
    }
    
    if (!user || !targetId || isLiking) return;

    setIsLiking(true);
    
    // Store current state for potential rollback
    const previousIsLiked = isLiked;
    const previousLikeCount = likeCount;
    
    // Optimistic update with immediate feedback
    const newIsLiked = !isLiked;
    const newLikeCount = newIsLiked ? likeCount + 1 : Math.max(likeCount - 1, 0);
    
    setIsLiked(newIsLiked);
    setLikeCount(newLikeCount);
    setOptimisticState({ isLiked: newIsLiked, likeCount: newLikeCount });
    
    // Show visual feedback animation
    setShowFeedback(true);
    setTimeout(() => setShowFeedback(false), 600);
    
    // Haptic feedback on mobile (if supported)
    if (isMobile) {
      triggerHapticFeedback(50);
    }
    
    // Notify parent immediately with optimistic update
    if (onLikeUpdate) {
      onLikeUpdate(targetId, newLikeCount, newIsLiked);
    }

    // Store optimistic state in localStorage for persistence across navigation
    const likeKey = `like_optimistic_${user.uid}_${targetType}_${targetId}`;
    localStorage.setItem(likeKey, JSON.stringify({
      isLiked: newIsLiked,
      likeCount: newLikeCount,
      timestamp: Date.now(),
      previousState: { isLiked: previousIsLiked, likeCount: previousLikeCount }
    }));

    // Immediately set loading state to false for better UX
    setIsLiking(false);

    // Process like operation in background (fire-and-forget)
    processLikeAsync(user.uid, targetType, targetId, newIsLiked, newLikeCount, previousIsLiked, previousLikeCount, likeKey)
      .then(({ success, serverData, shouldRollback }) => {
        if (shouldRollback) {
          // Only rollback if component is still mounted and user hasn't made another action
          const currentOptimistic = JSON.parse(localStorage.getItem(likeKey) || '{}');
          if (currentOptimistic.timestamp && Date.now() - currentOptimistic.timestamp < 30000) {
            setIsLiked(previousIsLiked);
            setLikeCount(previousLikeCount);
            setOptimisticState(null);
            
            if (onLikeUpdate) {
              onLikeUpdate(targetId, previousLikeCount, previousIsLiked);
            }
          }
        } else if (success && serverData) {
          // Sync with server data if different from optimistic
          if (serverData.isLiked !== newIsLiked || serverData.likeCount !== newLikeCount) {
            setIsLiked(serverData.isLiked);
            setLikeCount(serverData.likeCount);
            
            if (onLikeUpdate) {
              onLikeUpdate(targetId, serverData.likeCount, serverData.isLiked);
            }
          }
          setOptimisticState(null);
        }
        
        // Clean up localStorage
        localStorage.removeItem(likeKey);
      })
      .catch(error => {
        console.error('Background like processing failed:', error);
        // The operation will be retried by the background processor
      });

  }, [user, targetId, isLiking, authorId, isLiked, likeCount, onLikeUpdate, isMobile]);

  // Enhanced keyboard handler for accessibility
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleLike(e);
    }
  }, [handleLike]);

  // Create iOS Safari-compatible click handlers
  const likeHandlers = useMemo(() => 
    createIOSSafariClickHandler(handleLike), 
    [handleLike]
  );

  // Enhanced aria-label with text and count information
  const getAriaLabel = useCallback((isLiked, likeCount, targetType, displayText) => {
    const action = isLiked ? 'Unlike' : 'Like';
    const count = likeCount > 0 ? `, ${likeCount} likes` : '';
    const textInfo = displayText ? ` - ${displayText}` : '';
    return `${action} this ${targetType}${count}${textInfo}`;
  }, []);

  const ariaLabel = useMemo(() => 
    getAriaLabel(isLiked, likeCount, targetType, displayText),
    [isLiked, likeCount, targetType, displayText, getAriaLabel]
  );

  // Enhanced accessibility description
  const getAriaDescription = useCallback((isLiked, user) => {
    if (!user) return 'Sign in required to like content';
    return isLiked ? 'Click to remove your like' : 'Click to like this content';
  }, []);

  const ariaDescription = useMemo(() => 
    getAriaDescription(isLiked, user),
    [isLiked, user, getAriaDescription]
  );



  return (
    <button
      {...likeHandlers}
      onKeyDown={handleKeyDown}
      disabled={isLiking || !user}
      className={`
        group relative flex items-center justify-center ${config.gap} ${config.button} 
        ${config.minWidth} ${config.maxWidth} font-medium rounded-md transition-all duration-200 
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-800
        ${isLiked
          ? 'text-purple-600 dark:text-purple-400 bg-purple-50 hover:bg-purple-100 dark:bg-purple-900/20 dark:hover:bg-purple-900/30'
          : 'text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 hover:text-purple-600 dark:hover:text-purple-400'
        }
        ${isLiking ? 'opacity-75 cursor-wait' : ''}
        ${!user ? 'opacity-50 cursor-not-allowed' : ''}
        ${isMobile ? 'active:scale-95 active:bg-purple-200 dark:active:bg-purple-800/30' : ''}
        border border-gray-300 dark:border-gray-600 overflow-hidden
        ${className}
      `}
      style={{ touchAction: 'manipulation' }}
      aria-label={ariaLabel}
      aria-pressed={isLiked}
      aria-describedby={`like-button-desc-${targetId}`}
      role="button"
      tabIndex={0}
      title={!user ? 'Sign in to like' : ariaDescription}
    >
      {/* Enhanced flame icon with feedback animation */}
      <div className="relative">
        <AnimatedFlameIcon 
          className={`
            ${config.icon} transition-all duration-200
            ${isLiked ? 'scale-110' : 'hover:scale-110'}
            ${isLiking ? 'animate-pulse' : ''}
          `} 
          isLiked={isLiked} 
        />
        
        {/* Feedback animation overlay */}
        {showFeedback && (
          <div className="absolute inset-0 pointer-events-none">
            <AnimatedFlameIcon 
              className={`
                ${config.icon} text-purple-500 animate-ping opacity-75
              `} 
              isLiked={true}
            />
          </div>
        )}
      </div>

      {/* Text label with coordinated animations */}
      {displayText && (
        <div className="relative flex-shrink-0">
          <span className={`
            ${textClass} font-medium transition-all duration-200 transform truncate block
            ${isLiking ? 'opacity-75 animate-pulse scale-95' : ''}
            ${optimisticState ? 'opacity-75' : ''}
            ${isLiked ? 'text-purple-600 dark:text-purple-400 scale-105' : 'text-gray-600 dark:text-gray-400 scale-100'}
            hover:scale-105 group-hover:text-purple-600 dark:group-hover:text-purple-400
          `}
          title={displayText} // Show full text on hover if truncated
          >
            {displayText}
          </span>
          
          {/* Text feedback animation overlay - coordinates with icon feedback */}
          {showFeedback && (
            <span className={`
              absolute inset-0 ${textClass} font-medium text-purple-500 animate-ping opacity-75 pointer-events-none truncate block
            `}>
              {displayText}
            </span>
          )}
        </div>
      )}

      {/* Like count with loading state */}
      {showCount && (
        <span className={`
          ${config.text} font-medium transition-all duration-200
          ${isLiking ? 'opacity-75 animate-pulse' : ''}
          ${optimisticState ? 'opacity-75' : ''}
        `}>
          {likeCount}
        </span>
      )}

      {/* Screen reader support for non-text modes */}
      {!displayText && (
        <span className="sr-only">
          {isLiked ? 'Unlike' : 'Like'}
        </span>
      )}

      {/* Mobile loading indicator */}
      {isLiking && isMobile && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-gray-800/50 rounded-md">
          <div className="animate-spin rounded-full h-3 w-3 border-b border-purple-500"></div>
        </div>
      )}

      {/* Hidden description for screen readers */}
      <span 
        id={`like-button-desc-${targetId}`}
        className="sr-only"
      >
        {ariaDescription}. Current state: {isLiked ? 'liked' : 'not liked'}. 
        {likeCount > 0 && `Total likes: ${likeCount}.`}
        {displayText && ` Button shows: ${displayText}.`}
      </span>
    </button>
  );
};

// PropTypes for enhanced documentation and validation
LikeButton.propTypes = {
  // Existing props (unchanged for backward compatibility)
  targetType: PropTypes.oneOf(['question', 'reply']).isRequired,
  targetId: PropTypes.string.isRequired,
  initialLikeCount: PropTypes.number,
  authorId: PropTypes.string,
  onLikeUpdate: PropTypes.func,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  showCount: PropTypes.bool,
  className: PropTypes.string,
  
  // New optional props for enhanced functionality
  showText: PropTypes.bool,
  textVariant: PropTypes.oneOf(['full', 'icon-only', 'compact']),
  customText: PropTypes.shape({
    like: PropTypes.string,
    liked: PropTypes.string
  })
};

// Default props ensure backward compatibility
LikeButton.defaultProps = {
  initialLikeCount: 0,
  size: 'md',
  showCount: true,
  showText: true,
  textVariant: 'full',
  className: ''
};

export default LikeButton;